import { FC } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
} from "@mui/material";
import { makeSxStyles } from "ui";
import { useDialogStore } from "../../../../state/dialog-store";
import { DateTime } from "luxon";
import { formatStartTimeLabel } from "../../../../utils/date-time";

export interface BulkEditStartTimeDialogProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  children: React.ReactNode;
  description?: string;
  onConfirm: (onClose: () => void, value?: string) => void;
  onClose: () => void;
  open?: boolean;
  inputValue?: string;
  handler?: (value: string) => void;
  cancelText?: string;
  confirmText?: string;
  confirmButtonDisabled?: boolean;
  selectedTime?: string;
}

export const BulkEditStartTimeDialog: FC<BulkEditStartTimeDialogProps> = (
  props,
) => {
  const {
    title,
    sx,
    onConfirm,
    onClose,
    children,
    open,
    cancelText = "Cancel",
    confirmText = "Save",
    confirmButtonDisabled = false,
    selectedTime,
    ...rest
  } = props;
  const styles = useStyles();
  const { setDialog } = useDialogStore();

  // Handle bulk edit start time confirmation
  const handleConfirmClick = () => {
    if (!selectedTime) {
      // If no time selected, just call onConfirm directly
      onConfirm(onClose, "confirm");
      return;
    }

    const formattedTime = formatStartTimeLabel(
      DateTime.fromISO(`2000-01-01T${selectedTime}:00`),
    );

    setDialog({
      title: "Confirm Start Time Change",
      description: `Are you sure you want to change the start time for all routes to ${formattedTime}?`,
      confirmText: "Change Start Time",
      cancelText: "Cancel",
      onConfirm: async (confirmOnClose: () => void) => {
        onConfirm(onClose, "confirm");
        confirmOnClose();
      },
    });
  };

  return (
    <Dialog onClose={onClose} {...rest} sx={sx || {}} open={open || false}>
      <DialogTitle variant="titleLarge" sx={styles.dialogTitle}>
        {title}
      </DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ color: "#8ACEFF" }}
          data-testid="cancel-button"
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirmClick}
          variant="contained"
          sx={{
            backgroundColor: "#8ACEFF",
            color: "#00344E",
          }}
          disabled={confirmButtonDisabled}
          data-testid="confirm-button"
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  dialogTitle: {
    color: grey[100],
  },
}));
